import { CDN } from "../../utils/CDN.js";
import { VectorGraphics } from "./VectorGraphics.js"

/**
 * Static class with image related utilities
 * @memberof vef.ui.media
 * @author ckraemme <ckraemme@awi.de>
 * @author rhess <robin.hess@awi.de>
 */
class ImageUtils {
    /**
     * Select the matching resolution for a given width from an image srcset attribute containing width descriptors.
     * Reproduces the behavior of selecting an image with the srcset attribute
     * @param {string} srcset i.e., "https://.../image1.jpg 720w, https://.../image2.jpg 1920w, https://.../image3.jpg 3840w"
     * @param {string|Number} width in pixel i.e., "1800"
     * @returns {Array} list containing matching url and descriptor, i.e., ["https://.../image2.jpg", "1920w"]
     */
    static srcsetSelector(srcset, width) {

        var srcsetInfo = srcset.split(/,(?=\s|[a-zA-Z])/).map((item) => {
            const [url, descriptor] = item.trim().split(/\s+/);
            return [url, descriptor]
        });

        var naturalSort = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
        const index = 1;
        var numericDescriptor = srcsetInfo.map(item => item[index]);
        numericDescriptor.sort(naturalSort.compare);

        srcsetInfo.sort(function (firstElement, secondElement) {
            return numericDescriptor.indexOf(firstElement[index]) - numericDescriptor.indexOf(secondElement[index]);
        });

        let srcsetMatch = srcsetInfo[srcsetInfo.length - 1];
        for (let i = 0; i < numericDescriptor.length; i++) {
            if (parseInt(width) <= parseInt(numericDescriptor[i])) {
                srcsetMatch = srcsetInfo[i];
                break;
            }
        }
        return srcsetMatch
    }

    /**
     * Apply browser dependent srcset restrictions that cause bad performance in the pswp lightbox
     * @param {string} src image url
     * @param {string} srcset image srcset attribute with width descriptor
     * @returns {Array} [src, srcset]
     */
    static improveLightboxPerformance(src, srcset) {
        if (srcset) {
            const isChromium = !!window.chrome;
            if (!isChromium) {
                const maxWidth = 100000;
                let imageSrc;
                [imageSrc,] = ImageUtils.srcsetSelector(srcset, maxWidth);
                const imageFormat = imageSrc.split('.').pop();
                const isWebp = imageFormat.toLowerCase() == 'webp';
                if (isWebp) {
                    src = imageSrc;
                    srcset = null;
                }
            }
        }
        return [src, srcset]
    }

    /**
     * Simple method to display an image with support for a lightbox
     * @param {string} src image url
     * @param {number} width image width in pixel (ignores exif orientation, thus width > height for landscape)
     * @param {number} height image height in pixel (ignores exif orientation, thus width < height for portrait)
     * @param {string} [srcSet] image srcset attribute with width descriptor
     */
    static displayImage(src, width, height, srcset) {
        const [srcLightbox, srcsetLightbox] = ImageUtils.improveLightboxPerformance(src, srcset);
        const container = document.createElement("div");
        container.innerHTML = `
            <a data-pswp-width="${width}"
                data-pswp-height="${height}"
                ${srcsetLightbox ? ('data-pswp-srcset="' + srcsetLightbox + '"') : ""}
                target="_blank"
                href="${srcLightbox}"
                class="preview-thumbnail-image">
                <div style="padding-bottom: min(calc(${height}/${width} * 100%), 100%);"></div>
                <img
                    data-sizes="auto"
                    data-src="${src}"
                    ${srcset ? ('data-srcset="' + srcset + '"') : ""}
                    onerror='this.removeAttribute("onerror"); this.src="${VectorGraphics.errorImage('Photo')}"; this.removeAttribute("srcset"); this.parentElement.style="pointer-events: none"'
                    class="lazyload"
                    style="visibility:hidden; opacity: 0; transition: opacity 0.5s cubic-bezier(0, 0, 0.2, 1);"
                    onload="(function(e){e.style.visibility='visible'; e.style.opacity='1';})(this)">
            </a>
        `;

        const options = {
            gallery: container,
            children: 'a',
            pswpModule: () => CDN.import("Photoswipe"),
            wheelToZoom: true,
            bgOpacity: 0.98,
            initialZoomLevel: 'fit',
            secondaryZoomLevel: (zoomLevelObject) => {
                //Secondary zoom level is fill, but minimum 2 of fit
                return Math.max(2, zoomLevelObject.fill / zoomLevelObject.fit) * zoomLevelObject.fit
            },
            maxZoomLevel: 4.0,
            mainClass: "vef-ui viewer-lightbox"
        };

        CDN.import("lazysizes").then(lazysizes => {
            CDN.import("PhotoswipeLightbox").then(PhotoswipeLightbox => {
                const lightbox = new PhotoswipeLightbox.default(options);
                lightbox.on('uiRegister', function () {
                    lightbox.pswp.ui.registerElement({
                        name: 'zoom-level-indicator',
                        order: 8,
                        onInit: (el, pswp) => {
                            pswp.on('zoomPanUpdate', (e) => {
                                if (e.slide === pswp.currSlide) {
                                    el.innerText = 'Zoom: ' + Math.round(pswp.currSlide.currZoomLevel * 100) + '%';
                                }
                            });
                        }
                    })
                });

                lightbox.addFilter('contentErrorElement', (contentErrorElement, content) => {
                    const imageElement = document.createElement('img');
                    imageElement.src = VectorGraphics.errorImage('Photo');
                    imageElement.style.height = "100%";
                    imageElement.style.width = "100%";
                    imageElement.style.top = "0px";
                    imageElement.style.position = "absolute";
                    return imageElement;
                });

                lightbox.init();
            })
        })

        return container;
    }
}

export { ImageUtils };